import type { IReportCampaign } from '~/modules/campaign-analytics/types';
import type { IOrder, IOrderPageAnalytic } from '../../types';
import type { EAnalyticMode } from '~/core/gemxql';

import { ORDER_LISTING_COLUMNS, ORDER_LISTING_CONFIG } from '../../constants';
import { getFiltersForLatestOrderListing } from '../../helpers';
import { useStoreTotalOrders } from './useStoreTotalOrders';
import { useOrderParameters } from './useOrderParameters';
import { useGetVersionIds } from '../useGetVersionIds';
import { useQueryOrderListing } from '../../api';
import { useMemo } from 'react';

interface IUseFetchLatestOrdersProps {
  mode: EAnalyticMode;
  reportCampaign?: IReportCampaign;
  pageAnalytic?: IOrderPageAnalytic;
}

interface IUseFetchLatestOrdersReturn {
  totalStoreOrders: number;
  totalFilteredOrders: number;
  orderListingLatest: IOrder[];
  isLoadingLatest: boolean;
}

export const useFetchLatestOrders = ({
  reportCampaign,
  pageAnalytic,
  mode,
}: IUseFetchLatestOrdersProps): IUseFetchLatestOrdersReturn => {
  const { versionIds } = useGetVersionIds({ reportCampaign });

  const customFilters = useMemo(
    () => getFiltersForLatestOrderListing({ reportCampaign, pageAnalytic }),
    [reportCampaign, pageAnalytic],
  );

  const { totalStoreOrders } = useStoreTotalOrders({ mode });

  const { parameters: parametersForFilteredTotal } = useOrderParameters({
    metrics: ['orders'],
    customFilters,
    isLoading: pageAnalytic?.isLoading,
    mode,
    versionIds,
    isSyncSearchParams: false,
  });

  const { total: totalFilteredOrders } = useQueryOrderListing({
    variables: parametersForFilteredTotal,
    name: 'OrderListingTotalQL',
  });

  const { parameters: parametersForLatestOrders } = useOrderParameters({
    metrics: ORDER_LISTING_COLUMNS.map((column) => column.name),
    customFilters,
    mode,
    limit: ORDER_LISTING_CONFIG.SHOW_LATEST_ORDER_LISTING_AMOUNT,
    isSyncSearchParams: false,
    versionIds,
  });

  const isEnabled = !!reportCampaign || !!pageAnalytic;
  const { data: orderListingLatest, isLoading: isLoadingLatest } = useQueryOrderListing({
    variables: parametersForLatestOrders,
    enabled: isEnabled,
    name: 'OrderListingLatestQL',
  });

  return {
    totalStoreOrders,
    totalFilteredOrders,
    orderListingLatest,
    isLoadingLatest,
  };
};
